import { useAuth0 } from '@auth0/auth0-vue'
import { storeToRefs } from 'pinia'

import { computed, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConfigStore } from '@/legacy/store/modules/config'
import { authenticationGuard, pageTitleGuard } from '@/router/guard'
import { useFlagStore } from '@/store/flags/flags'
import { useFeatureFlags } from '@/store/flags/useFeatureFlags'
import { useDatadogUser } from '@/store/profile/datadogUser'
import { useEventServiceUser } from '@/store/profile/eventServiceUser'
import { useGlobalData } from '@/store/profile/globalData'
import { useWhoAmIInitialization } from '@/store/profile/whoAmI'
import { setupRouter, SET_QUERY_KEY } from '../libs/route/router'
import { wizardConfigs } from './wizard'

/**
 *
 */
export function setup() {
  const { setQuery } = setupRouter()
  provide(SET_QUERY_KEY, setQuery)

  const errorMessage =
    'Oops! There was an error loading the application. Please refresh the page to try again.'

  const configStore = useConfigStore()
  const flagStore = useFlagStore()
  const auth0 = useAuth0()
  const { configVals, isLoading: isConfigLoading } = storeToRefs(configStore)
  const { isLoaded: flagsAreLoaded, enableTutorials } = storeToRefs(flagStore)

  // Initialization hooks:
  // 1. Feature flags — watches auth email, initializes Split client
  // 2. WhoAmI — fetches current user identity once flags are loaded
  // 3. Side effects — sync user to Datadog + event service; fetch global catalogs (staff, payers)
  useFeatureFlags()
  const { isReady: whoAmIIsReady } = useWhoAmIInitialization()
  useDatadogUser()
  useEventServiceUser()
  useGlobalData()

  const router = useRouter()
  router.beforeEach(authenticationGuard)
  router.beforeEach(pageTitleGuard)

  const route = useRoute()
  const pageRequiresAuthentication = computed(() => !route.meta.unauthenticated)
  const pageClasses = computed(() => {
    // Pages that should take the full page without any scrollability, like a loading screen
    const OVERLAY_PAGES = ['/auth/callback']
    if (!OVERLAY_PAGES.includes(route.path)) {
      return ['overflow-auto']
    }
    return []
  })

  const showNavBar = computed(() => {
    return auth0.isAuthenticated.value || !pageRequiresAuthentication.value
  })

  const loadingErrorMessage = computed((): string => {
    let errorMessage: string | undefined

    if (isConfigLoading.value) {
      errorMessage = 'Loading configuration...'
    } else if (pageRequiresAuthentication.value) {
      if (!auth0.isAuthenticated.value) {
        errorMessage = 'Waiting on authentication (Auth0)...'
      } else if (!flagsAreLoaded.value) {
        errorMessage = 'Waiting on flags to load...'
      } else if (!whoAmIIsReady.value) {
        errorMessage = 'Waiting to load entity details from API...'
      }
    }

    if (errorMessage) {
      return errorMessage
    }
    return ''
  })

  return {
    isAuthenticated: auth0.isAuthenticated,
    configVals,
    pageRequiresAuthentication,
    loadingErrorMessage,
    pageClasses,
    errorMessage,
    showNavBar,
    enableTutorials,
    wizardConfigs,
  }
}
